import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Checkbox,
  IconButton,
  Divider,
  useTheme,
  InputAdornment,
  Link as MuiLink,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomTextField from "../components/RTLTextField";
import SocialLogin from "../components/SocialLogin";
import axios from "axios";
import notify from "../components/notify";
import loginBackground from "../assets/loginBackground.png";
import UserIcon from "@mui/icons-material/PersonOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/MailOutline";
import LockIcon from "@mui/icons-material/Lock";
import { signup } from "../redux/slices/user/thunk";
import { useDispatch } from "react-redux";

const SignUpScreen = () => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isArabic = i18n.language === "ar";
  const dispatch = useDispatch();

  const [showPassword, setShowPassword] = useState(false);

  const user =
    JSON.parse(localStorage.getItem("user")) ||
    JSON.parse(sessionStorage.getItem("user"));
  const API_URL = `${process.env.REACT_APP_API_BASE_URL}/api/v1`;
  const lang = localStorage.getItem("i18nextLng") === "en" ? "en" : "ar";

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t("signup.errors.name_min"))
      .required(t("signup.errors.name_required")),
    email: Yup.string()
      .email(t("signup.errors.email_invalid"))
      .required(t("signup.errors.email_required")),
    mobile: Yup.string()
      .matches(/^[0-9]{8,15}$/, t("signup.errors.phone_invalid"))
      .required(t("signup.errors.phone_required")),
    password: Yup.string()
      .min(8, t("signup.errors.password_min"))
      .required(t("signup.errors.password_required")),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      email: "",
      mobile: "",
      password: "",
      role: "client", // القيمة الافتراضية كما في الـ object الخاص بك
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      // تجهيز الكائن تماماً كما طلبته
      const signupData = {
        name: values.name.trim(),
        email: values.email.trim(),
        mobile: values.mobile.trim(),
        password: values.password,
        role: values.role,
      };

      try {
        const response = await dispatch(signup({ data: signupData })).unwrap();

        // if (response.status === 200 || response.status === 201) {
          notify(t("signup.success"), "success");
          navigate("/login");
        // }
      } catch (error) {
        const msg = error.response?.data?.message || t("signup.failed");
        notify(msg, "error");
      } finally {
        setSubmitting(false);
      }
    },
  });

  if (user) return <Navigate to="/home" replace />;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        position: "relative",
        bgcolor: "background.default",
        backgroundImage: { xs: "none", md: `url(${loginBackground})` },
        backgroundSize: "cover",
      }}
    >
      {/* Overlay للمتصفحات الكبيرة */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "absolute",
          inset: 0,
          background: theme.palette.primary.gradient,
          opacity: 0.75,
          zIndex: 1,
        }}
      />

      <Container
        maxWidth="xs"
        disableGutters
        sx={{
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: { xs: 0, sm: 4 },
            bgcolor: "background.paper",
            flexGrow: { xs: 1, sm: 0 },
            margin: "auto",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          <Box sx={{ textAlign: "center", mb: 4 }}>
            <Typography variant="h4" fontWeight={800} gutterBottom>
              {t("Create Account")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("Join us today and start your journey")}
            </Typography>
          </Box>

          <form onSubmit={formik.handleSubmit}>
            {/* Full Name */}
            <CustomTextField
              fullWidth
              name="name"
              label={t("Full Name")}
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <UserIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Email */}
            <CustomTextField
              fullWidth
              name="email"
              label={t("Email address")}
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Mobile */}
            <CustomTextField
              fullWidth
              name="mobile"
              label={t("Mobile Number")}
              value={formik.values.mobile}
              onChange={formik.handleChange}
              error={formik.touched.mobile && Boolean(formik.errors.mobile)}
              helperText={formik.touched.mobile && formik.errors.mobile}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PhoneIcon />
                  </InputAdornment>
                ),
              }}
            />

            {/* Password */}
            <CustomTextField
              fullWidth
              name="password"
              label={t("Password")}
              type={showPassword ? "text" : "password"}
              value={formik.values.password}
              onChange={formik.handleChange}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              size="large"
              variant="contained"
              type="submit"
              disabled={formik.isSubmitting}
              sx={{
                py: 2,
                borderRadius: 2,
                fontSize: "1rem",
                fontWeight: 700,
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              }}
            >
              {formik.isSubmitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t("Create Account")
              )}
            </Button>

            <Box sx={{ mt: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                {t("Already have an account?")}{" "}
                <MuiLink
                  onClick={() => navigate("/login")}
                  sx={{
                    fontWeight: 700,
                    cursor: "pointer",
                    textDecoration: "none",
                    color: "primary.main",
                  }}
                >
                  {t("Login")}
                </MuiLink>
              </Typography>
            </Box>
            {/* 
            <Box sx={{ my: 3 }}>
              <Divider>
                <Typography variant="caption" color="text.secondary">{t("OR")}</Typography>
              </Divider>
            </Box>

            <SocialLogin formik={formik} /> */}
          </form>
        </Paper>
      </Container>
    </Box>
  );
};

export default SignUpScreen;
